import { useMemo } from 'react'

import { FilterOperator } from '@tribeplatform/gql-client/types'

import { useEventModuleAvailability } from '../../Event/hooks/useEventModuleAvailability.js'
import {
  getFormattedJSONDate,
  MEMBER_SEARCH_CONFIG_INPUT,
  POSTS_FIELDS,
  SMART_SEARCH_QUERY_LIMIT,
  SmartSearchEntityType,
  SmartSearchFilters,
  useInfiniteSearchMembers,
  useInfiniteSearchPosts,
  useInfiniteSearchSpaces,
} from '../index.js'

import { useInfiniteSearchEvents } from './useInfiniteSearchEvents.js'
import { useSearchExternal } from './useSearchExternal.js'

type Props = {
  query: string
  date?: string
  tagsId?: string[]
  enabled?: boolean
  type?: SmartSearchFilters['type']
  spaceIds?: string[]
  officialResourcesSpaceIds?: string[]
}

export const useQuerySmartSearch = ({
  query,
  date,
  tagsId,
  enabled = true,
  type,
  spaceIds,
  officialResourcesSpaceIds,
}: Props) => {
  const hasQuery = useMemo(() => !!query.length, [query])

  const canSearchInPosts = useMemo(
    () => !type || type === SmartSearchEntityType.POSTS,
    [type],
  )
  const canSearchInMembers = useMemo(
    () => !type || type === SmartSearchEntityType.MEMBERS,
    [type],
  )
  const canSearchInSpaces = useMemo(
    () => !type || type === SmartSearchEntityType.SPACES,
    [type],
  )

  const canSearchInExternals = useMemo(
    () => !type || type === SmartSearchEntityType.EXTERNALS,
    [type],
  )

  const { isEventModuleEnabledOnNetwork } = useEventModuleAvailability()

  const canSearchInEvents =
    (!type || type === SmartSearchEntityType.EVENTS) &&
    isEventModuleEnabledOnNetwork

  const commonPostsFilterExpressions = useMemo(
    () => [
      // exclude replies
      {
        key: 'parentPostId',
        isNull: true,
      },
      ...(date
        ? [
            {
              key: 'publishedAt',
              range: { gte: getFormattedJSONDate(date) },
            },
          ]
        : []),
      ...(tagsId?.length
        ? [
            {
              key: 'tagIds',
              any: tagsId.map(tagId => JSON.stringify(tagId)),
            },
          ]
        : []),
    ],
    [date, tagsId],
  )

  const officialPostsQueryFilter = useMemo(() => {
    if (
      !canSearchInPosts ||
      !officialResourcesSpaceIds ||
      officialResourcesSpaceIds?.length === 0
    ) {
      return {
        spaceIds: [],
        filterExpressions: [],
      }
    }

    /**
     * If official spaces are provided, only look into the spaces that are allowed
     * by the space configurations of the search context
     */
    const queryableSpaceIdsForOfficialPosts = spaceIds?.length
      ? officialResourcesSpaceIds?.filter(spaceId => spaceIds.includes(spaceId))
      : officialResourcesSpaceIds

    return {
      spaceIds: queryableSpaceIdsForOfficialPosts,
      filterExpressions: queryableSpaceIdsForOfficialPosts?.length
        ? [
            ...commonPostsFilterExpressions,
            {
              key: 'spaceId',
              any: queryableSpaceIdsForOfficialPosts.map(spaceId =>
                JSON.stringify(spaceId),
              ),
            },
          ]
        : [],
    }
  }, [
    spaceIds,
    officialResourcesSpaceIds,
    commonPostsFilterExpressions,
    canSearchInPosts,
  ])

  const regularPostsFilterExpressions = useMemo(() => {
    if (!canSearchInPosts) {
      return []
    }

    /**
     * If search context is configured to only look into a subset of spaces
     * 1. Exclude the spaces that are queried for official posts, when searching through regular posts.
     * 2. Otherwise,
     * 2.1 If official spaces are set, exclude them from the regular results return an "except" operator .
     * 2.2 Otherwise (and since are no limitations set on the spaces), we can search through all posts -> commonPostsFilterExpressions
     */
    const queryableSpaceIdsForRegularPosts = spaceIds?.length
      ? spaceIds.filter(
          spaceId => !officialPostsQueryFilter?.spaceIds.includes(spaceId),
        )
      : []

    if (queryableSpaceIdsForRegularPosts.length > 0) {
      return [
        ...commonPostsFilterExpressions,
        {
          key: 'spaceId',
          any: queryableSpaceIdsForRegularPosts.map(spaceId =>
            JSON.stringify(spaceId),
          ),
        },
      ]
    }

    if (officialPostsQueryFilter?.spaceIds?.length > 0) {
      return [
        ...commonPostsFilterExpressions,
        {
          key: 'spaceId',
          except: officialPostsQueryFilter.spaceIds.map(spaceId =>
            JSON.stringify(spaceId),
          ),
        },
      ]
    }

    return [...commonPostsFilterExpressions]
  }, [
    spaceIds,
    officialPostsQueryFilter.spaceIds,
    commonPostsFilterExpressions,
    canSearchInPosts,
  ])

  const officialPostsQuery = useInfiniteSearchPosts({
    variables: {
      query,
      limit: SMART_SEARCH_QUERY_LIMIT,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [
              ...(officialPostsQueryFilter?.filterExpressions ?? []),
            ],
            operator: FilterOperator.And,
          },
        ],
      },
    },
    fields: POSTS_FIELDS,
    useInfiniteQueryOptions: {
      enabled:
        enabled &&
        hasQuery &&
        !!officialPostsQueryFilter?.filterExpressions?.length,
    },
  })

  const postsQuery = useInfiniteSearchPosts({
    variables: {
      query,
      limit: SMART_SEARCH_QUERY_LIMIT,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [...regularPostsFilterExpressions],
            operator: FilterOperator.And,
          },
        ],
      },
    },
    fields: POSTS_FIELDS,
    useInfiniteQueryOptions: {
      enabled: enabled && hasQuery && !!regularPostsFilterExpressions?.length,
    },
  })

  const membersQuery = useInfiniteSearchMembers({
    variables: {
      searchConfig: MEMBER_SEARCH_CONFIG_INPUT,
      query,
      limit: SMART_SEARCH_QUERY_LIMIT,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [
              ...(date
                ? [
                    {
                      key: 'joinedAt',
                      range: { gte: getFormattedJSONDate(date) },
                    },
                  ]
                : []),
            ],
            operator: FilterOperator.And,
          },
        ],
      },
    },
    fields: {
      nodes: {
        profilePicture: 'all',
        role: 'all',
      },
      pageInfo: 'all',
    },
    useInfiniteQueryOptions: {
      enabled: hasQuery && enabled && canSearchInMembers,
    },
  })

  const spacesQuery = useInfiniteSearchSpaces({
    variables: {
      query,
      limit: SMART_SEARCH_QUERY_LIMIT,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [
              ...(date
                ? [
                    {
                      key: 'createdAt',
                      range: { gte: getFormattedJSONDate(date) },
                    },
                  ]
                : []),
            ],
            operator: FilterOperator.And,
          },
        ],
      },
    },
    fields: {
      nodes: {
        address: 'all',
        image: {
          onImage: 'all',
          onGlyph: 'all',
        },
      },
      pageInfo: 'all',
    },
    useInfiniteQueryOptions: {
      enabled: hasQuery && enabled && canSearchInSpaces,
    },
  })

  const eventsQuery = useInfiniteSearchEvents({
    variables: {
      query,
      limit: SMART_SEARCH_QUERY_LIMIT,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [
              ...(date
                ? [
                    {
                      key: 'createdAt',
                      range: { gte: getFormattedJSONDate(date) },
                    },
                  ]
                : []),
              ...(tagsId?.length
                ? [
                    {
                      key: 'tagIds',
                      any: tagsId.map(tagId => JSON.stringify(tagId)),
                    },
                  ]
                : []),
            ],
            operator: FilterOperator.And,
          },
        ],
      },
    },
    fields: {
      pageInfo: 'all',
      nodes: {
        images: 'all',
        createdBy: {
          member: {
            profilePicture: {
              onGlyph: 'all',
              onFile: 'all',
              onImage: {
                urls: 'all',
              },
            },
          },
        },
        space: {
          address: 'basic',
          image: {
            onImage: 'all',
            onGlyph: 'all',
          },
        },
      },
    },
    useInfiniteQueryOptions: {
      enabled: hasQuery && enabled && canSearchInEvents,
    },
  })

  const externalsSearchQuery = useSearchExternal({
    variables: {
      query,
    },
    fields: 'all',
    useQueryOptions: {
      enabled: hasQuery && enabled && canSearchInExternals,
    },
  })

  const meta = {
    hasOfficialPosts:
      !officialPostsQuery.isLoading &&
      !officialPostsQuery.isError &&
      (officialPostsQuery.data?.pages[0]?.nodes?.length ?? 0) > 0,
    hasPosts:
      !postsQuery.isLoading &&
      !postsQuery.isError &&
      (postsQuery.data?.pages[0]?.nodes?.length ?? 0) > 0,
    hasMembers:
      !membersQuery.isLoading &&
      !membersQuery.isError &&
      (membersQuery.data?.pages[0]?.nodes?.length ?? 0) > 0,
    hasSpaces:
      !spacesQuery.isLoading &&
      !spacesQuery.isError &&
      (spacesQuery.data?.pages[0]?.nodes?.length ?? 0) > 0,
    hasExternal:
      !externalsSearchQuery.isLoading &&
      !externalsSearchQuery.isError &&
      (externalsSearchQuery.data?.length ?? 0) > 0,
    hasEvents:
      !eventsQuery.isLoading &&
      !eventsQuery.isError &&
      (eventsQuery.data?.pages[0]?.nodes?.length ?? 0) > 0,
  }

  // a method to check if there are results in a specific type
  // if no type is provided, return true if any of the queries has results and none of them is loading
  // if a type is provided, return true if the query has results for that type and the query is not loading
  // returns undefined if the query(s) is loading
  const hasResultsByType = (
    type: SmartSearchFilters['type'],
  ): boolean | undefined => {
    switch (type) {
      case SmartSearchEntityType.OFFICIAL_POSTS:
        return officialPostsQuery.isLoading || officialPostsQuery.isError
          ? undefined
          : meta.hasOfficialPosts
      case SmartSearchEntityType.POSTS:
        return postsQuery.isLoading || postsQuery.isError
          ? undefined
          : meta.hasPosts
      case SmartSearchEntityType.MEMBERS:
        return membersQuery.isLoading || membersQuery.isError
          ? undefined
          : meta.hasMembers
      case SmartSearchEntityType.SPACES:
        return spacesQuery.isLoading || spacesQuery.isError
          ? undefined
          : meta.hasSpaces
      case SmartSearchEntityType.EXTERNALS:
        return externalsSearchQuery.isLoading || externalsSearchQuery.isError
          ? undefined
          : !!meta.hasExternal
      case SmartSearchEntityType.EVENTS:
        return eventsQuery.isLoading || eventsQuery.isError
          ? undefined
          : meta.hasEvents
      default:
        // if one of the queries is loading, return undefined
        if (
          officialPostsQuery.isLoading ||
          postsQuery.isLoading ||
          membersQuery.isLoading ||
          spacesQuery.isLoading ||
          externalsSearchQuery.isLoading ||
          officialPostsQuery.isError ||
          postsQuery.isError ||
          membersQuery.isError ||
          spacesQuery.isError ||
          externalsSearchQuery.isError ||
          eventsQuery.isError
        ) {
          return undefined
        }

        // if one of the queries has results, return true
        return (
          (officialPostsQuery.data?.pages[0]?.nodes?.length ?? 0) > 0 ||
          (postsQuery.data?.pages[0]?.nodes?.length ?? 0) > 0 ||
          (membersQuery.data?.pages[0]?.nodes?.length ?? 0) > 0 ||
          (spacesQuery.data?.pages[0]?.nodes?.length ?? 0) > 0 ||
          (eventsQuery.data?.pages[0]?.nodes?.length ?? 0) > 0 ||
          (externalsSearchQuery.data?.length ?? 0) > 0
        )
    }
  }

  return {
    officialPostsQuery,
    postsQuery,
    membersQuery,
    spacesQuery,
    externalsSearchQuery,
    eventsQuery,
    meta,
    hasResultsByType,
  }
}
